'use strict';
import actionTypes from './actionTypes';

if (typeof window !== 'undefined') {
    var $ = window.$;
}

function goToPage(pageNumber) {
    return function(dispatch, getState) {
        const state = getState().searchReducer;
        const filters = state.filters || {};

        $.ajax({
            url: '/search/items/ajax',
            type: 'GET',
            data: {
                pageSize: state.pageSize,
                page: pageNumber,
                tags: '',
                withChildItems: state.withChildItems,
                sort: state.sort,
                keywords: state.keywords,
                minimumPrice: filters.minimumPrice,
                maximumPrice: filters.maximumPrice,
                categories: filters.categories,
                providerType: filters.providerType,
                badges: filters.badges,
                location: filters.location,
                serviceableArea: filters.serviceableArea,
                rating: filters.rating,
                customValues: filters.customValues,
                sellerId: filters.sellerId,
                startTimestamp: state.startTimestamp,
                endTimestamp: state.endTimestamp,
                userLatitude: state.userLatitude,
                userLongitude: state.userLongitude,
                isAllDates: state.isAllDates
            },
            success: function(result) {
                return dispatch({
                    type: actionTypes.GO_TO_PAGE,
                    items: result.data.items,
                    pageNumber: pageNumber
                });
            },
            error: function(jqXHR, textStatus, errorThrown) {
                console.log('error', textStatus, errorThrown);
                // Revert URL to previous page on failure
                const urlParams = new URLSearchParams(window.location.search);
                urlParams.set('page', state.pageNumber);
                window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
            }
        });
    };
}

function sortResult(sort) {
    const queryParams = new URLSearchParams(window.location.search);
    const page = queryParams.get('page');
    const sortOption = queryParams.get('sort');

    return function(dispatch, getState) {
        const state = getState().searchReducer;
        const filters = state.filters || {};

        $.ajax({
            url: '/search/items/ajax',
            type: 'GET',
            data: {
                pageSize: state.pageSize,
                page: parseInt(page) ?? state.pageNumber,
                tags: [],
                withChildItems: state.withChildItems,
                sort: sortOption ?? sort,
                keywords: state.keywords,
                minimumPrice: filters.minimumPrice,
                maximumPrice: filters.maximumPrice,
                categories: filters.categories,
                providerType: filters.providerType,
                badges: filters.badges,
                location: filters.location,
                serviceableArea: filters.serviceableArea,
                rating: filters.rating,
                customValues: filters.customValues,
                sellerId: filters.sellerId,
                startTimestamp: state.startTimestamp,
                endTimestamp: state.endTimestamp,
                userLatitude: state.userLatitude,
                userLongitude: state.userLongitude,
                isAllDates: state.isAllDates
            },
            success: function(result) {
                return dispatch({
                    type: actionTypes.SORT_SEARCH_RESULT,
                    items: result.data.items,
                    sort: sort,
                    pageNumber: 1
                });
            },

            error: function(jqXHR, textStatus, errorThrown) {
                console.log(textStatus, errorThrown);
            }
        });
    };
}

function changeResultDisplay(resultDisplayBehavior) {
    return {
        type: actionTypes.SET_SEARCH_RESULT_DISPLAY,
        resultDisplayBehavior: resultDisplayBehavior
    };
}

function searchByCategory(categories) {
    let categoryIds = [];

    function getCustomFields(categoryIds, callback) {
        $.ajax({
            url: '/search/items/custom-fields',
            type: 'GET',
            data: {
                categoryIds: categoryIds
            },
            success: function(result) {
                callback(result);
            },

            error: function(jqXHR, textStatus, errorThrown) {
                console.log(textStatus, errorThrown);
            }
        });
    }

    function getCategoryHierarchy(category, hierarchy, hierarchyCount) {
        if (hierarchy) {
            if (hierarchy.indexOf(category.ID) >= 0 || hierarchyCount > hierarchy.length) {
                if (category.ChildCategories) {
                    hierarchyCount = hierarchyCount + 1;
                    category.ChildCategories.forEach(function(c) {
                        getCategoryHierarchy(c, hierarchy, hierarchyCount);
                    });
                }

                categoryIds.push(category.ID);
            }
        } else {
            if (category.ChildCategories) {
                category.ChildCategories.forEach(function(c) {
                    getCategoryHierarchy(c);
                });
            }

            categoryIds.push(category.ID);
        }
    }

    // MKTPLC-228: state.categories holds only TOP-LEVEL categories, so a sub-category
    // ID never matched the old top-level find() — it returned undefined and
    // getCategoryHierarchy(undefined) threw, so sub-category clicks ran no search.
    // Walk the full nested tree instead, and fall back to the raw ID if not found.
    function findCategoryInTree(nodes, id) {
        for (const node of nodes || []) {
            if (node.ID == id) return node;
            const found = findCategoryInTree(node.ChildCategories, id);
            if (found) return found;
        }
        return null;
    }

    return function(dispatch, getState) {
        const state = getState().categoryReducer;

        Array.from(categories).forEach(function(category) {
            const node = findCategoryInTree(state.categories, category.ID);
            if (node) {
                getCategoryHierarchy(node);
            } else {
                categoryIds.push(category.ID);
            }
        });

        $.ajax({
            url: '/search/items/ajax',
            type: 'GET',
            data: {
                pageSize: getState().searchReducer.pageSize,
                page: 1,
                tags: '',
                withChildItems: getState().searchReducer.withChildItems,
                sort: process.env.PRICING_TYPE != 'service_level' ? 'item_desc' : 'nearest',
                //keywords: state.keywords,
                //minPrice: state.minimumPrice,
                //maxPrice: state.maximumPrice,
                categories: categoryIds,
                customValues: null,
                keywords: categories[0].keywords,
                // MKTPLC-228: location-scope category searches the same way the filter
                // path does. When `categories` are present the server skips geocoding
                // location/serviceableArea (SearchService.js), so these raw coordinates
                // are the ONLY geo source — without them the search returns 0 results.
                location: getState().searchReducer.location,
                serviceableArea: getState().searchReducer.serviceableArea,
                userLatitude: getState().searchReducer.userLatitude,
                userLongitude: getState().searchReducer.userLongitude
            },
            success: function(promiseItemsResult) {
                getCustomFields(categoryIds, function(result) {
                    // MKTPLC-228: unwrap the { data: { items } } envelope so the reducer's
                    // action.items.Records / .TotalRecords resolve (mirrors searchByFilters).
                    const items = promiseItemsResult.data.items;
                    return dispatch({
                        type: actionTypes.SEARCH_BY_CATEGORY,
                        items: items,
                        selectedCategories: categories,
                        customFilters: result
                    });
                });
            },
            error: function(jqXHR, textStatus, errorThrown) {
                console.log(textStatus, errorThrown);
            }
        });
    };
}

function searchByFilters(filters, cb = () => {}) {
    return function(dispatch, getState) {
        const state = getState().searchReducer;

        $.ajax({
            url: '/search/items/ajax',
            type: 'GET',
            data: {
                pageSize: state.pageSize,
                page: 1,
                tags: '',
                withChildItems: state.withChildItems,
                sort: state.sort,
                keywords: state.keywords,
                minimumPrice: filters.minimumPrice,
                maximumPrice: filters.maximumPrice,
                categories: filters.categories,
                providerType: filters.providerType,
                badges: filters.badges,
                location: filters.location,
                serviceableArea: filters.serviceableArea,
                rating: filters.rating,
                customValues: filters.customValues,
                sellerId: filters.sellerId,
                startTimestamp: state.startTimestamp,
                endTimestamp: state.endTimestamp,
                userLatitude: state.userLatitude,
                userLongitude: state.userLongitude,
                isAllDates: state.isAllDates
            },
            success: function(result) {
                cb({ data: result });
                return dispatch({
                    type: actionTypes.SEARCH_BY_FILTERS,
                    items: result.data.items,
                    filters
                });
            },

            error: function(jqXHR, textStatus, errorThrown) {
                console.log(textStatus, errorThrown);
                cb({ error: textStatus });
            }
        });
    };
}

function searchGooglePlaces(keyword, callback) {
    return function(dispatch, getState) {
        $.ajax({
            url: '/search/google-places',
            type: 'GET',
            data: {
                keyword: keyword
            },
            success: function(result) {
                callback(result);

                return dispatch({
                    type: ''
                });
            },
            error: function(jqXHR, textStatus, errorThrown) {
                console.log(textStatus, errorThrown);
            }
        });
    };
}

function searchSuggestedItems(lat, lng, callback) {
    return function(dispatch, getState) {
        const state = getState().searchReducer;

        $.ajax({
            url: '/search/suggested-items',
            type: 'GET',
            data: {
                userLatitude: lat,
                userLongitude: lng
            },
            success: function(result) {
                callback(result);

                return dispatch({
                    type: ''
                });
            },

            error: function(jqXHR, textStatus, errorThrown) {
                console.log(textStatus, errorThrown);
            }
        });
    };
}

export { sortResult, changeResultDisplay, goToPage, searchByCategory, searchByFilters, searchGooglePlaces, searchSuggestedItems };

export default {
    sortResult,
    changeResultDisplay,
    goToPage,
    searchByCategory,
    searchByFilters,
    searchGooglePlaces,
    searchSuggestedItems
};
